import { UserRole } from '~features/PrerenderUser';
import { RenderProofExperience } from './RenderProofExperience';

/**
 * Roles that must not be offered the proof here.
 *
 * Both actions on this banner reach past the person taking them: Dismiss writes
 * account-scoped state for every colleague, and View calls an endpoint that
 * queues a billable render. The wizard route already excludes Guests, and the
 * repo disables mutating controls for these two roles elsewhere.
 */
const READ_ONLY_ROLES: string[] = [UserRole.Guest, UserRole.BillingManager];

const RENDER_PROOF_ENTRY_POINT_WINDOW_DAYS = 14;

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

export type RenderProofEntryPointEligibilityInput = {
  experience: RenderProofExperience;
  role: string;
  trackingCodeInstalledDate: Date | string | null;
  /**
   * False while the account's stored state is unknown — not loaded yet, or the
   * request failed. Treating unknown as "not seen" would re-offer the proof to
   * an account that already had it whenever `/v3/ui-state` is unavailable.
   */
  isSeenStateKnown: boolean;
  viewed: boolean;
  dismissed: boolean;
  now: Date;
};

function daysSince(from: Date, now: Date): number {
  return (now.getTime() - from.getTime()) / MILLISECONDS_PER_DAY;
}

/**
 * Whether this account is *a candidate* for the entry point.
 *
 * Candidacy is not the same as showing the banner: it says only that the
 * account is in the arm, inside the window, and has not been served yet. The
 * component then looks for an actual proof and shows the banner only if one
 * exists, so the offer is never made against nothing.
 *
 * There is deliberately no render-count precondition. The obvious source for
 * one is the `/me` snapshot in Redux, which is not refetched mid-session: an
 * account whose first render lands *after* the wizard gave up would sit at a
 * stale zero and never see the banner — precisely the case this entry point
 * exists to catch.
 */
export function isRenderProofEntryPointEligible(
  input: RenderProofEntryPointEligibilityInput,
): boolean {
  const {
    experience,
    role,
    trackingCodeInstalledDate,
    isSeenStateKnown,
    viewed,
    dismissed,
    now,
  } = input;

  if (experience !== 'test_experience') return false;
  if (READ_ONLY_ROLES.includes(role)) return false;
  if (!isSeenStateKnown) return false;
  if (viewed || dismissed) return false;
  if (trackingCodeInstalledDate === null) return false;

  const integratedAt = new Date(trackingCodeInstalledDate);
  // An unparseable or future-dated integration is not a window this offer sits
  // inside: a negative age would otherwise sail under the upper bound.
  if (!Number.isFinite(integratedAt.getTime())) return false;
  if (integratedAt > now) return false;

  return daysSince(integratedAt, now) <= RENDER_PROOF_ENTRY_POINT_WINDOW_DAYS;
}
